/* eslint-disable react-refresh/only-export-components --
 * This file exports a hook + a re-named provider alongside the context
 * value type. Splitting them would push consumers across files for no
 * functional benefit; HMR for these tiny shells is not load-bearing.
 */
import { createContext, useContext } from 'react';

/**
 * Coordination between a section's sub-tab strip and its cards. Used by
 * any section that declares `subsections` (Demographics, Medical History,
 * Consent & Governance — the pattern is generic).
 *
 * Cards can be open simultaneously: `openIds` is the authoritative set
 * of currently-open managed cards. Cards whose `anchorId` is in
 * `managedIds` read their open state from `openIds` and call `setOpen`
 * on toggle. The tab strip:
 *
 *  - Renders a filled-dot indicator on tabs whose card is in `openIds`,
 *    so the strip doubles as a "what's currently expanded" overview.
 *  - On click, calls `setOpen(id, true)` then `scrollTo(id)` — opening
 *    the card if it was closed, and bringing it into view either way.
 *    No exclusive selection; other open cards are left alone.
 *
 * Cards in sections without a provider (no subsections) keep their
 * existing uncontrolled open/closed state.
 */
export interface SubsectionContextValue {
  readonly openIds: ReadonlySet<string>;
  readonly managedIds: ReadonlySet<string>;
  readonly setOpen: (anchorId: string, open: boolean) => void;
  readonly scrollTo: (anchorId: string) => void;
}

const SubsectionContext = createContext<SubsectionContextValue | null>(null);

export const SubsectionProvider = SubsectionContext.Provider;

export function useSubsectionContext(): SubsectionContextValue | null {
  return useContext(SubsectionContext);
}
